import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { checkPromoCode } from '@apiCalls';
import { handleRenderError, withErrorHandler } from '@utils/errorHandler';

interface IPromoCodeCheck {
  defaultPromoCode?: string;
  eventId: string | number;
  onPromoCodeChanged: (promoCode: string) => void;
}

export function PromoCodeCheck({
  defaultPromoCode,
  eventId,
  onPromoCodeChanged,
}: IPromoCodeCheck) {
  try {
    const queryClient = useQueryClient();
    const [appliedPromoCode, setAppliedPromoCode] = useState<string>();
    const [promoCode, setPromoCode] = useState(defaultPromoCode ?? '');

    const {
      data: response,
      error,
      isLoading,
      isError,
    } = useQuery({
      queryKey: ['checkPromoCode', eventId, appliedPromoCode],
      queryFn: () => checkPromoCode(eventId, appliedPromoCode as string),
      enabled: !!appliedPromoCode,
    });

    const handleVerify = async () => {
      setAppliedPromoCode(promoCode);
    };

    useEffect(() => {
      if (
        response?.IsValid &&
        !!appliedPromoCode &&
        typeof onPromoCodeChanged === 'function'
      ) {
        onPromoCodeChanged(appliedPromoCode);
      } else if (isError || response?.IsValid === false) {
        onPromoCodeChanged('');
      }
    }, [isError, response]);

    return (
      <div className='eventPromoCodeCheck'>
        <form
          className='formPromoCodeCheck'
          onSubmit={withErrorHandler(uiEvent => {
            uiEvent.preventDefault();
            handleVerify();
          })}
        >
          <div className='form-row' id='checkPromoCodeRow'>
            <div
              className={`form-field${promoCode === '' ? '' : ' isNotEmpty'}`}
            >
              <label htmlFor='checkPromoCode'>
                Promo Code{' '}
                {response?.IsValid || defaultPromoCode ? 'Accepted' : ''}
              </label>
              <input
                type='text'
                id='checkPromoCode'
                name='checkPromoCode'
                value={promoCode}
                onChange={uiEvent => {
                  setAppliedPromoCode(undefined);
                  queryClient.invalidateQueries({
                    queryKey: ['checkPromoCode', eventId],
                  });
                  setPromoCode(uiEvent.target.value);
                }}
              />
            </div>
          </div>
          {isLoading ? (
            <div className='buttonProceed muted blocked'>
              <p>Checking...</p>
            </div>
          ) : response?.IsValid || defaultPromoCode ? (
            <div className='buttonProceed blocked'>
              <p>Accepted</p>
            </div>
          ) : (
            <div
              className={`buttonProceed ${promoCode ? '' : 'blocked'}`}
              onClick={withErrorHandler(handleVerify)}
            >
              <p>Verify</p>
            </div>
          )}
        </form>
        {(isError || !response?.IsValid) && (
          <>
            <p
              style={{
                color: 'rgb(var(--secondary-colour))',
                paddingLeft: 16,
                paddingTop: 8,
              }}
            >
              {error?.message ?? response?.Reason}
            </p>
          </>
        )}
      </div>
    );
  } catch (error) {
    return handleRenderError(error);
  }
}
